import { Component, computed, effect, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DistributionWidgetResponse } from 'dashboard/components/visuals-dashboard/widgets/distribution-widget/distribution-widget-data-response.interface';
import { getDashboardColorByPercentage } from 'dashboard/enums/visuals/visuals-dashboard-colors.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualDashboardDistributor } from 'dashboard/interfaces/visuals/visual-dashboard-distributor.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import {
    BarChartRow,
    BarChartVariantEnum,
    IceVisualsDashboardBarChartComponent,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-bar-chart/ice-visuals-dashboard-bar-chart.component';

@Component({
    selector: 'app-distribution-widget',
    imports: [IceVisualsDashboardBarChartComponent, TranslateModule],
    templateUrl: './distribution-widget.component.html',
    styleUrl: './distribution-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class DistributionWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    barChartVariantEnum = BarChartVariantEnum;
    onLangChange = toSignal(this.translate.onLangChange);

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    selectedSize = computed(() => {
        const widget = this.widget();
        return widget.selectedSize;
    });

    stackedVariantColumns = signal([{ key: 'percentage', label: '' }]);

    showTablePhotosColumn = computed(() => {
        const widgetSettings = this.widget().imageTrackerDistributionSettings;
        return widgetSettings ? widgetSettings.isPhotos : false;
    });

    showTableListingsColumn = computed(() => {
        const widgetSettings = this.widget().imageTrackerDistributionSettings;
        return widgetSettings ? widgetSettings.isListings : false;
    });

    showAnyTableColumn = computed(() => {
        return this.showTablePhotosColumn() || this.showTableListingsColumn();
    });

    distributorIdsToShow = computed<number[] | null>(() => {
        const widgetSettings = this.widget().imageTrackerDistributionSettings;
        return widgetSettings ? widgetSettings.distributorIds : null;
    });

    stackedVariantRows = computed(() => {
        const data = this.dataService.data();
        this.onLangChange(); // Re-evaluate on language change
        if (!data) {
            return [];
        }
        return [
            {
                label: this.translate.instant('dashboard.visuals.widgets.distribution.listingsDistributed'),
                values: {
                    percentage: {
                        value: data.listingsDistributionOverallScore,
                        color: getDashboardColorByPercentage(data.listingsDistributionOverallScore),
                    },
                },
            },
            {
                label: this.translate.instant('dashboard.visuals.widgets.distribution.photosDistributed'),
                values: {
                    percentage: {
                        value: data.imageDistributionOverallScore,
                        color: getDashboardColorByPercentage(data.imageDistributionOverallScore),
                    },
                },
            },
        ];
    });

    tableVariantColumns = computed(() => {
        this.onLangChange(); // Re-evaluate on language change
        const columns = [];
        if (this.showTablePhotosColumn()) {
            columns.push({ key: 'photos', label: this.translate.instant('dashboard.visuals.widgets.distribution.photos') });
        }
        if (this.showTableListingsColumn()) {
            columns.push({ key: 'listings', label: this.translate.instant('dashboard.visuals.widgets.distribution.listings') });
        }
        return columns;
    });

    tableVariantRows = computed(() => {
        const data = this.dataService.data();
        this.onLangChange(); // Re-evaluate on language change
        if (!data) {
            return [];
        }
        const distributorIdsToShow = this.distributorIdsToShow();
        const filteredDataRanges = distributorIdsToShow
            ? data.ranges.filter((range) => distributorIdsToShow.includes(range.distributorId))
            : data.ranges;
        const rows: BarChartRow[] = filteredDataRanges.map((range) => ({
            label: range.distributorName,
            values: {
                listings: {
                    value: range.listingsDistributionPercentage,
                    color: getDashboardColorByPercentage(range.listingsDistributionPercentage),
                },
                photos: {
                    value: range.imageDistributionPercentage,
                    color: getDashboardColorByPercentage(range.imageDistributionPercentage),
                },
            },
        }));
        rows.push({
            label: this.translate.instant('dashboard.visuals.widgets.distribution.overall'),
            values: {
                listings: {
                    value: data.listingsDistributionOverallScore,
                    color: getDashboardColorByPercentage(data.listingsDistributionOverallScore),
                },
                photos: {
                    value: data.imageDistributionOverallScore,
                    color: getDashboardColorByPercentage(data.imageDistributionOverallScore),
                },
            },
            isTotal: true,
        });
        return rows;
    });

    availableDistributors = computed<VisualDashboardDistributor[]>(() => {
        const data = this.dataService.data();
        if (!data) {
            return [];
        }

        const distributorMap = new Map<number, string>();
        data.ranges.forEach((range) => {
            if (!distributorMap.has(range.distributorId)) {
                distributorMap.set(range.distributorId, range.distributorName);
            }
        });

        const sorted = Array.from(distributorMap, ([distributorId, distributorName]) => ({
            distributorId,
            distributorName,
        })).sort((entryA, entryB) => entryA.distributorName.localeCompare(entryB.distributorName));

        return sorted;
    });

    constructor(
        public dataService: VisualsDashboardDataService<DistributionWidgetResponse>,
        private visualsDashboardService: VisualsDashboardService,
        private translate: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
        effect(() => {
            const availableDistributors = this.availableDistributors();
            const widgetHtmlId = this.widget().htmlId;
            if (widgetHtmlId && availableDistributors !== null) {
                this.visualsDashboardService.setAvailableDistributorsForHtmlId(widgetHtmlId, availableDistributors);
            }
        });
    }
}
